import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import {catchError, map, Observable, of, tap, throwError, timeout, TimeoutError} from 'rxjs';
import {environment} from '../../../environments/environment';
import {UserRegistrationDto} from './UserRegistrationDto';
import {CompetitionDto} from './CompetitionDto';
import {FeaturedCompetitionPublicDto} from './FeaturedCompetitionPublicDto';
import {CompetitionPodiumDto} from './CompetitionPodiumDto';
import {CompetitionInvitePreviewDto} from './CompetitionInvitePreviewDto';

@Injectable({
  providedIn: 'root'
})
export class CompetitionService {

  // Built off HttpBackend directly (same pattern as public-stats.service.ts)
  // to bypass every HTTP interceptor, notably AuthInterceptor. Used by
  // getFeaturedCompetitionPublic(): the public homepage section must receive
  // the same slim public payload (id, name, description, startDate, endDate,
  // status, participantCount, featured) for every visitor, logged in or not.
  // Also used by getInvitePreview()/getInvitePreviewById() so the public
  // /join landing gets the same anonymous fetch for every visitor, and a 401
  // from it never trips AuthInterceptor's logout.
  // Also used by getMyCompetitionsQuiet() (backlog #303): the homepage
  // featured card's /mine read, with the JWT attached manually, for the same
  // reason — a dead token there must not log the visitor out.
  // getFeaturedCompetition() deliberately stays on the ordinary, interceptor-
  // routed this.http — /competition's join dialog reads competition.isPrivate
  // (competition.component.ts), which the slim anonymous payload omits, so
  // that surface needs the authenticated full CompetitionDTO, not this one.
  // See backend docs/api-contract.md, GET /points/featuredcompetition.
  private readonly anonymousHttp: HttpClient;

  constructor(private http: HttpClient, backend: HttpBackend) {
    this.anonymousHttp = new HttpClient(backend);
  }

  createCompetition(competition: CompetitionDto): Observable<HttpResponse<CompetitionDto>> {
    return this.http.post<CompetitionDto>(`${environment.apiURL}/points/competition`, competition, {observe: 'response'}).pipe(
      tap((response: HttpResponse<CompetitionDto>) => {
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }

  getActiveCompetition(notionUserId: string): Observable<CompetitionDto> {
    return this.http.get<CompetitionDto>(`${environment.apiURL}/points/competition?notionUserId=${notionUserId}`)
      .pipe(
        timeout(5000),  // Set a timeout of 5000 milliseconds (5 seconds)
        catchError(error => {
          if (error instanceof TimeoutError) {
            return throwError(() => new Error('Request timed out'));
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Backlog #234: the list-shaped read behind the /competition page's move to
   * rendering every ACTIVE-or-UPCOMING competition the user belongs to, not
   * just one. Backend counterpart: notion-habit-challenge!267
   * (backlog/234-competitions-mine). The one-competition cap is still active
   * server-side, so today this returns at most one entry — the array shape is
   * forward-looking for a later, separately sequenced cap raise.
   *
   * Transitional fallback: if the backend serving this session predates !267
   * (GET /points/competitions/mine 404s — route doesn't exist yet), fall back
   * to the single-competition read and adapt its result into the same array
   * shape. Do not special-case 401 here — AuthInterceptor already logs the
   * user out on any non-login 401 before this ever runs.
   */
  getMyCompetitions(notionUserId: string): Observable<CompetitionDto[]> {
    const params = new HttpParams().set('notionUserId', notionUserId);
    return this.http.get<CompetitionDto[]>(`${environment.apiURL}/points/competitions/mine`, { params })
      .pipe(
        catchError(error => {
          if (error.status === 404) {
            return this.getActiveCompetition(notionUserId).pipe(
              map(competition => (competition ? [competition] : [])),
              catchError(() => of([]))
            );
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Backlog #303: the homepage featured card's own /mine read. Built off
   * anonymousHttp (see getFeaturedCompetitionPublic above) with the JWT
   * attached by hand, for the same reason the featured-public fetch and the
   * invite previews use anonymousHttp — a dead token must not trip
   * AuthInterceptor's logout on a marketing surface. No 404 fallback here
   * (unlike getMyCompetitions): the endpoint is deployed everywhere that
   * matters, so any error is the caller's problem to swallow.
   *
   * getMyCompetitions() above keeps going through the ordinary,
   * interceptor-routed http on purpose — on /competition, logging a visitor
   * out on a dead token is the wanted behavior.
   */
  getMyCompetitionsQuiet(notionUserId: string, token: string): Observable<CompetitionDto[]> {
    const params = new HttpParams().set('notionUserId', notionUserId);
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    return this.anonymousHttp.get<CompetitionDto[]>(`${environment.apiURL}/points/competitions/mine`, { params, headers });
  }

  leaveCompetition(notionUserId: string, competitionId: number): Observable<HttpResponse<any>> {
    const params = new HttpParams()
      .set('notionUserId', notionUserId)
      .set('competitionId', competitionId);

    return this.http.post(`${environment.apiURL}/points/leavecompetition`, null,
      {params, observe: 'response', responseType: 'text'}).pipe(
      tap((response: HttpResponse<any>) => {
        // handle response here
      }),
      catchError((error) => {
        return throwError(error);
      })
    );
  }
  joinCompetition(notionUserId: string, competitionId: number, password: string | null): Observable<CompetitionDto> {
    const params = new HttpParams()
      .set('notionUserId', notionUserId)
      .set('competitionId', competitionId.toString())
      .set('password', password ?? '');

    return this.http.post<CompetitionDto>(`${environment.apiURL}/points/joincompetition`, null, {params});
  }

  joinCompetitionViaInvite(notionUserId: string, inviteToken: string): Observable<CompetitionDto> {
    const params = new HttpParams()
      .set('notionUserId', notionUserId)
      .set('inviteToken', inviteToken);

    return this.http.post<CompetitionDto>(`${environment.apiURL}/points/joininvite`, null, {params});
  }

  findCompetitionsByName(name: string): Observable<CompetitionDto[]> {
    return this.http.get<CompetitionDto[]>(`${environment.apiURL}/points/competitions?name=${name}`);
  }

  searchCompetitions(searchTerm: string): Observable<CompetitionDto[]> {
    const params = new HttpParams().set('searchTerm', searchTerm);
    return this.http.get<CompetitionDto[]>(`${environment.apiURL}/points/searchcomp`, { params });
  }

  getAllCompetitions(): Observable<CompetitionDto[]> {
    return this.http.get<CompetitionDto[]>(`${environment.apiURL}/points/allcompetitions`);
  }

  getFeaturedCompetition(): Observable<CompetitionDto> {
    return this.http.get<CompetitionDto>(`${environment.apiURL}/points/featuredcompetition`);
  }

  /**
   * The caller's latest completed non-group competition podium; the backend
   * answers 404 when there is none, mapped to null. See backend
   * docs/api-contract.md, GET /points/competition/podium.
   */
  getCompetitionPodium(notionUserId: string): Observable<CompetitionPodiumDto | null> {
    const params = new HttpParams().set('notionUserId', notionUserId);
    return this.http
      .get<CompetitionPodiumDto>(`${environment.apiURL}/points/competition/podium`, { params })
      .pipe(
        catchError((err) =>
          err instanceof HttpErrorResponse && err.status === 404 ? of(null) : throwError(() => err)
        )
      );
  }

  getFeaturedCompetitionPublic(): Observable<FeaturedCompetitionPublicDto> {
    return this.anonymousHttp.get<FeaturedCompetitionPublicDto>(`${environment.apiURL}/points/featuredcompetition`);
  }

  /**
   * Public (no-auth) preview of a competition invite by token; the backend
   * responds 404 for an unknown token or a non-shareable competition. Built
   * off anonymousHttp (see getFeaturedCompetitionPublic above) so the
   * join page renders the same way for logged-out and logged-in visitors,
   * and the timeout/catchError shape mirrors DuelService.getInvitePreview.
   */
  getInvitePreview(inviteToken: string): Observable<CompetitionInvitePreviewDto> {
    const params = new HttpParams().set('inviteToken', inviteToken);
    return this.anonymousHttp.get<CompetitionInvitePreviewDto>(`${environment.apiURL}/points/competition/invite`, { params }).pipe(
      timeout(5000),
      catchError(error => {
        if (error instanceof TimeoutError) {
          return throwError(() => new Error('Request timed out'));
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Same preview, addressed by competitionId instead of inviteToken — used
   * by the /join page for links shared as `?competitionId=`. Exactly one of
   * inviteToken/competitionId must be sent; never both.
   */
  getInvitePreviewById(competitionId: string): Observable<CompetitionInvitePreviewDto> {
    const params = new HttpParams().set('competitionId', competitionId);
    return this.anonymousHttp.get<CompetitionInvitePreviewDto>(`${environment.apiURL}/points/competition/invite`, { params }).pipe(
      timeout(5000),
      catchError(error => {
        if (error instanceof TimeoutError) {
          return throwError(() => new Error('Request timed out'));
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Emails a join invite for a competition the caller is a member of. Mirrors
   * DuelService.sendInviteEmail's shape (JWT-authenticated POST, response
   * discarded as text). Addressed by competitionId, not inviteToken —
   * competitions (unlike duels) key their invite email by id; see
   * POST /points/competition/invite-email in docs/api-contract.md.
   */
  sendCompetitionInviteEmail(notionUserId: string, competitionId: number, email: string): Observable<HttpResponse<any>> {
    const params = new HttpParams()
      .set('notionUserId', notionUserId)
      .set('competitionId', competitionId.toString())
      .set('email', email);
    return this.http.post(
      `${environment.apiURL}/points/competition/invite-email`, null,
      { params, observe: 'response', responseType: 'text' }
    ).pipe(
      catchError((error) => throwError(() => error))
    );
  }
}
